
import { unwrapApiRecord } from '../shared/utils/apiResponse';

import { userApi as api } from './apiInstances';

/**
 * Transaction Reconfirmation Service
 * Handles frontend-initiated transaction verification after payment gateway completion
 */
class TransactionReconfirmationService {
    /**
     * Reconfirm a transaction after payment completion (Finalize/Verify)
     * Uses PUT /user/transactions/{id}
     * @param {string} transactionId - The transaction identifier
     * @param {string} paymentGateway - Payment gateway used (Stripe, Paystack, Flutterwave, Wallet)
     * @param {Object} gatewayData - Gateway-specific data for verification
     * @returns {Promise<Object>} Verification result
     */
    async reconfirmTransaction(transactionId: string, paymentGateway: string, gatewayData: unknown) {
        try {
            // detailed payload mapping to match ReconfirmTransactionRequest
            // The controller expects:
            // payment_gateway (string)
            // gateway_data (array)

            const payload: Record<string, unknown> = {
                payment_gateway: paymentGateway,
                gateway_data: gatewayData
            };

            const data = gatewayData as Record<string, unknown>;

            // Ensure Stripe payload has payment_intent_id inside gateway_data
            if (paymentGateway === 'Stripe' && data?.payment_intent) {
                const gatewayDataObj = payload.gateway_data as Record<string, unknown>;
                gatewayDataObj.payment_intent_id = typeof data.payment_intent === 'string'
                    ? data.payment_intent
                    : (data.payment_intent as { id: string }).id;
            }

            // Paystack: Ensure reference/save_card_details are inside gateway_data
            // This is primarily handled by the caller, but re-confirming structure here is good practice if needed.
            // Current callers pass flattened gatewayData which is exactly what we put into gateway_data key now.



            const response = await api('POST', `/user/transactions/${transactionId}/reconfirm`, payload, { _silent: 'true' } as Record<string, string>);

            const responseData = unwrapApiRecord<Record<string, unknown>>((response as Record<string, unknown>)?.data);
            const status = typeof responseData.status === 'string' ? responseData.status : '';

            if (response && (response.success || status === 'successful')) {

                return {
                    success: true,
                    data: responseData,
                    message: (response as { message?: string }).message || 'Transaction successful'
                };
            }

            return {
                success: false,
                pending: status === 'pending' || status === 'processing',
                status,
                retryable: responseData.retryable !== false,
                requiresAction: responseData.requires_action === true,
                error: (response as { error?: string }).error || (response as { message?: string }).message || 'Verification failed',
                data: responseData
            };

        } catch (error: unknown) {
            console.error('Transaction finalization error:', error);
            return {
                success: false,
                error: (error as Error).message || 'Failed to finalize transaction',
                data: null
            };
        }
    }

    /**
     * Verify and Sync Status
     * For PUT implementation, this is largely a wrapper around reconfirmTransaction
     * since PUT is synchronous.
     */
    async verifyAndSyncStatus({ transactionId, paymentGateway, gatewayData }: { transactionId: string, paymentGateway: string, gatewayData: unknown }) {
        // 1. Attempt finalization (PUT)
        // No fallback polling - relying solely on PUT
        const result = await this.reconfirmTransaction(transactionId, paymentGateway, gatewayData);

        if (result.success) {
            return {
                success: true,
                data: {
                    transaction: result.data, // The PUT returns the full transaction model
                    reconfirmation: result.data
                },
                message: (result as { message?: string }).message
            };
        }

        // If PUT failed, we return the failure immediately without polling fallback
        return result;
    }

    /**
     * Check transaction status
     * @param {string} transactionId - The transaction identifier
     * @returns {Promise<Object>} Transaction status
     */
    async getTransactionStatus(transactionId: string) {
        try {
            const response = await api('GET', `/user/transactions/${transactionId}/status`);
            const responseData = unwrapApiRecord<Record<string, unknown>>((response as Record<string, unknown>)?.data);

            if (response.success) {
                return {
                    success: true,
                    status: responseData.status,
                    transaction: responseData.transaction
                };
            }

            return {
                success: false,
                error: (response as { error?: string }).error || 'Failed to get transaction status'
            };

        } catch (error: unknown) {
            console.error('Transaction status check error:', error);
            return {
                success: false,
                error: (error as Error).message || 'Failed to check transaction status'
            };
        }
    }

    /**
     * Quick status check (lightweight)
     * @param {string} transactionId - The transaction identifier
     * @returns {Promise<Object>} Quick status result
     */
    async quickStatusCheck(transactionId: string) {
        try {
            const response = await api('GET', `/user/transactions/${transactionId}/quick-status`);
            const res = response as Record<string, unknown>;

            return {
                success: true,
                transactionId: res.transaction_id,
                status: res.status,
                confirmed: res.confirmed,
                amount: res.amount,
                currency: res.currency,
                paymentGateway: res.payment_gateway
            };

        } catch (error: unknown) {

            return {
                success: false,
                error: (error as Error).message || 'Failed to check transaction status'
            };
        }
    }

    /**
     * Handle successful Stripe payment
     * @param {string} transactionId - Transaction identifier
     * @param {Object} paymentIntent - Stripe payment intent
     * @returns {Promise<Object>} Reconfirmation result
     */
    async handleStripeSuccess(transactionId: string, paymentIntent: { id: string }) {
        const gatewayData = {
            payment_intent_id: paymentIntent.id,
            payment_intent: paymentIntent.id
        };

        return this.reconfirmTransaction(transactionId, 'Stripe', gatewayData);
    }

    /**
     * Handle successful Paystack payment
     * @param {string} transactionId - Transaction identifier
     * @param {Object} response - Paystack response
     * @param {boolean} saveCardDetails - Whether to save card details
     * @returns {Promise<Object>} Reconfirmation result
     */
    async handlePaystackSuccess(transactionId: string, response: { reference: string, transaction?: string }, saveCardDetails = false) {
        const gatewayData = {
            reference: response.reference,
            transaction_id: response.transaction || transactionId,
            save_card_details: saveCardDetails
        };

        return this.reconfirmTransaction(transactionId, 'Paystack', gatewayData);
    }

    /**
     * Handle successful Flutterwave payment
     * @param {string} transactionId - Transaction identifier
     * @param {Object} response - Flutterwave response
     * @returns {Promise<Object>} Reconfirmation result
     */
    async handleFlutterwaveSuccess(transactionId: string, response: { flw_ref: string, transaction_id?: string }) {
        const gatewayData = {
            flw_ref: response.flw_ref,
            transaction_id: response.transaction_id || transactionId
        };

        return this.reconfirmTransaction(transactionId, 'Flutterwave', gatewayData);
    }

    /**
     * Handle successful Wallet payment
     * @param {string} transactionId - Transaction identifier
     * @param {string} pin - User's wallet PIN
     * @returns {Promise<Object>} Reconfirmation result
     */
    async handleWalletSuccess(transactionId: string, pin: string) {
        const gatewayData = {
            pin: pin
        };

        return this.reconfirmTransaction(transactionId, 'Wallet', gatewayData);
    }

    /**
     * Confirm pending transactions for a specific game
     * @param {string|number} gameId
     */
    async confirmGameTransactions(gameId: string | number) {
        try {
            const response = await api('POST', `/user/games/${encodeURIComponent(gameId)}/transactions/confirm`);
            return {
                success: response.success,
                message: (response as { message?: string }).message,
                data: response.data,
            };
        } catch (error: unknown) {
            console.error('confirmGameTransactions error:', error);
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            return {
                success: false,
                error: err.response?.data?.message || err.message || 'Unable to confirm payments for this game',
            };
        }
    }

    /**
     * Poll transaction status with retry logic
     * @param {string} transactionId - Transaction identifier
     * @param {Object} options - Polling options
     * @returns {Promise<Object>} Final transaction status
     */
    /**
     * Single poll attempt execution
     */
    private async executePollAttempt(
        transactionId: string,
        targetStatus: string,
        onStatusChange: ((result: unknown) => void) | null
    ): Promise<{ stop: boolean; result?: unknown }> {
        try {
            const result = await this.quickStatusCheck(transactionId);

            if (result.success) {
                if (onStatusChange) {
                    onStatusChange(result);
                }

                if (result.status === targetStatus) {
                    return {
                        stop: true,
                        result: {
                            success: true,
                            status: result.status,
                            confirmed: result.confirmed
                        }
                    };
                }

                if (result.status === 'failed') {
                    return {
                        stop: true,
                        result: {
                            success: false,
                            status: result.status,
                            error: 'Transaction failed'
                        }
                    };
                }
            }
            return { stop: false };
        } catch (error) {
            console.error(`Polling attempt failed:`, error);
            return { stop: false };
        }
    }

    /**
     * Poll transaction status with retry logic
     * @param {string} transactionId - Transaction identifier
     * @param {Object} options - Polling options
     * @returns {Promise<Object>} Final transaction status
     */
    async pollTransactionStatus(transactionId: string, options: {
        maxAttempts?: number;
        interval?: number;
        onStatusChange?: ((result: unknown) => void) | null;
        targetStatus?: string;
    } = {}) {
        const {
            maxAttempts = 10,
            interval = 2000,
            onStatusChange = null,
            targetStatus = 'successful'
        } = options;

        let attempts = 0;

        while (attempts < maxAttempts) {
            const { stop, result } = await this.executePollAttempt(transactionId, targetStatus, onStatusChange);

            if (stop && result) {
                return { ...(result as Record<string, unknown>), attempts: attempts + 1 };
            }

            attempts++;

            if (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, interval));
            }
        }

        return {
            success: false,
            error: 'Polling timeout - transaction status could not be confirmed',
            attempts
        };
    }

    /**
     * Batch reconfirm multiple transactions
     * @param {Array} transactions - Array of transaction data
     * @returns {Promise<Object>} Batch reconfirmation result
     */
    async batchReconfirm(transactions: unknown[]) {
        try {
            const response = await api('POST', '/user/transactions/batch-reconfirm', {
                transactions
            });
            const responseData = unwrapApiRecord<Record<string, unknown>>((response as Record<string, unknown>)?.data);

            return {
                success: response.success,
                message: (response as { message?: string }).message,
                results: responseData.results,
                summary: responseData.summary
            };

        } catch (error: unknown) {
            console.error('Batch reconfirmation error:', error);
            return {
                success: false,
                error: (error as Error).message || 'Batch reconfirmation failed'
            };
        }
    }
}

// Create a singleton instance
const transactionReconfirmationService = new TransactionReconfirmationService();

export default transactionReconfirmationService;
